import clsx from "clsx";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import { describeError } from "@/api/client";
import { useCanEdit, useWorkspace } from "@/api/session";
import {
  Badge,
  Button,
  ButtonLink,
  Checkbox,
  DataTable,
  EmptyState,
  Icon,
  IconButton,
  Money,
  PageHeader,
  Select,
  TextField,
  Toolbar,
  Tooltip,
  useToast,
  type DataTableColumn,
  type RowAction,
} from "@/components";
import { formatInteger, MISSING_VALUE, pluralize } from "@/lib/format";
import { countryName } from "@/lib/regions";
import { useSearchParamsInPlace } from "@/lib/hooks";
import {
  useAddToList,
  useShoppingList,
  useSupplyCategories,
  useSupplySearch,
  type FacetDef,
  type SearchQuery,
  type SupplyRow,
  type UnitBasis,
} from "../api";
import { FacetPanel } from "../components/FacetPanel";
import { MarketOverview } from "../components/MarketOverview";
import { OfferPrice } from "../components/OfferPrice";
import { SupplyDrawer } from "../components/SupplyDrawer";
import {
  CATEGORY_COLUMNS,
  columnHeader,
  conditionToParam,
  formatSpec,
  missingUnitPriceReason,
  perUnitLabel,
  priceCheckText,
  rangeFromParam,
  unitPriceDecimals,
  unitSymbol,
  valuesFromParam,
} from "../format";
import styles from "./SuppliesPage.module.css";

const PAGE_SIZE = 50;
// URL parameters the page keeps for itself; the rest go to /api/supplies/search as they are.
const PAGE_PARAMS = new Set(["item", "page", "ships", "asked", "unread"]);
const KEPT_ON_NEW_QUESTION = ["in_stock", "ships", "sort"];

function sortOptions(basis: UnitBasis | null) {
  return [
    { value: "unit_price", label: basis ? `Lowest price ${perUnitLabel(basis).toLowerCase()}` : "Lowest unit price" },
    { value: "price", label: "Lowest pack price" },
    { value: "capacity", label: "Capacity" },
    { value: "supplier", label: "Supplier" },
    { value: "title", label: "Name" },
  ];
}

function searchQuery(params: URLSearchParams): SearchQuery {
  const query: SearchQuery = {};
  params.forEach((value, key) => {
    if (!PAGE_PARAMS.has(key) && value) query[key] = value;
  });
  if (params.get("ships") === "1") query.ships_to_me = "1";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  query.limit = String(PAGE_SIZE);
  query.offset = String((page - 1) * PAGE_SIZE);
  return query;
}

/** Every crawled supply, searchable by spec: a sentence ("glass vials 5–15 ml with PTFE liners") or
 * the filters of a category, with offers in the workspace currency and the price per piece, litre
 * or kg. */
export function SuppliesPage() {
  const [params, setParams] = useSearchParamsInPlace();
  const workspace = useWorkspace();
  const country = workspace?.ship_to.country || workspace?.market_country || "CA";
  const canEdit = useCanEdit();
  const toast = useToast();
  const query = useMemo(() => searchQuery(params), [params]);
  const search = useSupplySearch(query);
  const taxonomy = useSupplyCategories();
  const list = useShoppingList();
  const addToList = useAddToList();
  const [draft, setDraft] = useState(params.get("asked") ?? "");
  const category = params.get("category") ?? "";
  const data = search.data;
  const shownCategory = data?.category || category;
  const categoryDef = taxonomy.data?.find((entry) => entry.slug === shownCategory);
  const basis = categoryDef?.unit_basis ?? null;
  const facetDefs = new Map((categoryDef?.facets ?? []).map((facet) => [facet.key, facet]));

  function update(change: (next: URLSearchParams) => void, { keepPage = false, toTop = false } = {}) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        change(next);
        if (!keepPage) next.delete("page");
        return next;
      },
      { preventScrollReset: !toTop },
    );
  }

  const setParam = (name: string, value: string | null, options?: { keepPage?: boolean }) =>
    update((next) => (value ? next.set(name, value) : next.delete(name)), { ...options, toTop: name === "page" });

  // A sentence is read into a category and filters once; the URL then holds them as plain filters,
  // so the panel shows them and unchecking one sticks.
  const interpreted = data?.interpreted;
  useEffect(() => {
    if (!interpreted || !params.get("text")) return;
    update((next) => {
      next.set("asked", next.get("text") ?? "");
      next.delete("text");
      if (interpreted.rest) next.set("unread", interpreted.rest);
      else next.delete("unread");
      if (interpreted.category) next.set("category", interpreted.category);
      for (const [key, condition] of Object.entries(interpreted.filters)) next.set(key, conditionToParam(condition));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interpreted]);

  function ask(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    setParams(
      (current) => {
        const next = new URLSearchParams();
        for (const key of KEPT_ON_NEW_QUESTION) {
          const value = current.get(key);
          if (value) next.set(key, value);
        }
        if (text) next.set("text", text);
        return next;
      },
      { replace: false },
    );
  }

  function clearAll() {
    setDraft("");
    setParams((current) => {
      const next = new URLSearchParams();
      for (const key of KEPT_ON_NEW_QUESTION) {
        const value = current.get(key);
        if (value) next.set(key, value);
      }
      return next;
    });
  }

  function chooseCategory(slug: string) {
    update((next) => {
      const keep = ["q", "in_stock", "ships", "sort", "supplier"];
      for (const key of [...next.keys()]) if (!keep.includes(key)) next.delete(key);
      if (slug) next.set("category", slug);
    });
  }

  function toggleSupplier(slug: string) {
    const chosen = valuesFromParam(params.get("supplier"));
    const next = chosen.includes(slug) ? chosen.filter((item) => item !== slug) : [...chosen, slug];
    setParam("supplier", next.length ? next.join(",") : null);
  }

  function searchTitles(words: string) {
    update((next) => {
      next.set("q", [next.get("q"), words].filter(Boolean).join(" "));
      next.delete("unread");
    });
  }

  function add(row: SupplyRow) {
    addToList.mutate(
      { variant_id: row.variant_id, quantity: 1 },
      {
        onSuccess: () =>
          toast.success(`Added to your shopping list`, { description: `${row.title} from ${row.supplier.name}` }),
        onError: (error) => toast.error("Couldn’t add it to the list", { description: describeError(error) }),
      },
    );
  }

  // Built on every render: a cell's handler built in an earlier one would hand setSearchParams that
  // render's search params and drop the filters set since.
  const specKeys = CATEGORY_COLUMNS[shownCategory] ?? ["capacity_ml"];
  const specColumns: DataTableColumn<SupplyRow>[] = specKeys
    .map((key) => facetDefs.get(key))
    .filter((facet): facet is FacetDef => facet !== undefined)
    .map((facet) => ({
      id: facet.key,
      header: columnHeader(facet),
      numeric: facet.kind === "number",
      cell: (row) => <SpecCell row={row} facet={facet} />,
    }));
  const countsPieces = !shownCategory || facetDefs.has("pack_count");
  const columns: DataTableColumn<SupplyRow>[] = [
    {
      id: "title",
      header: "Supply",
      width: "wide",
      cell: (row) => (
        <span className={styles.titleCell}>
          <button type="button" className={styles.open} onClick={() => setParam("item", row.id, { keepPage: true })}>
            {row.title}
          </button>
          <span className={styles.sub}>
            {[row.variant_title, row.sku].filter(Boolean).join(" · ") || MISSING_VALUE}
          </span>
          <span className={styles.supplier}>
            {row.supplier.name}
            {row.supplier.ships_to_workspace === false ? (
              <Badge tone="warning">Doesn’t ship to {countryName(country)}</Badge>
            ) : null}
            {row.us_glass_surtax ? <Badge tone="warning">US-made glass: 50% surtax</Badge> : null}
            {row.price_check ? (
              <Tooltip content={priceCheckText(row.price_check)}>
                <button type="button" className={styles.hint}>
                  <Badge tone="warning">Check pack size</Badge>
                </button>
              </Tooltip>
            ) : null}
          </span>
        </span>
      ),
    },
    ...(shownCategory && !categoryDef?.includes.length
      ? []
      : [
          {
            id: "category",
            header: "Category",
            cell: (row: SupplyRow) =>
              taxonomy.data?.find((entry) => entry.slug === row.category)?.label ?? row.category,
          },
        ]),
    ...specColumns,
    ...(countsPieces
      ? [
          {
            id: "pack",
            header: "Per pack",
            numeric: true,
            cell: (row: SupplyRow) =>
              row.pack_count ? formatInteger(row.pack_count) : <span className={styles.unknown}>{MISSING_VALUE}</span>,
          },
        ]
      : []),
    {
      id: "price",
      header: "Pack price",
      numeric: true,
      cell: (row) => <OfferPrice offer={row.offer} />,
    },
    {
      id: "unit",
      header: basis ? perUnitLabel(basis) : "Per unit",
      numeric: true,
      cell: (row) => <UnitPriceCell row={row} columnBasis={basis} />,
    },
    {
      id: "stock",
      header: "Stock",
      width: "fit",
      cell: (row) =>
        row.offer.in_stock === null ? (
          <Badge dashed>Unknown</Badge>
        ) : row.offer.in_stock ? (
          <Badge tone="success">In stock</Badge>
        ) : (
          <Badge>Out of stock</Badge>
        ),
    },
  ];

  const rowActions = (row: SupplyRow): RowAction<SupplyRow>[] => [
    {
      label: row.in_list ? "Add another pack" : "Add to shopping list",
      icon: "cart",
      onSelect: add,
      disabled: !canEdit,
    },
    { label: "Details…", icon: "info", onSelect: () => setParam("item", row.id, { keepPage: true }) },
    {
      label: "Open in the store",
      icon: "externalLink",
      onSelect: () => window.open(row.url, "_blank", "noopener"),
      disabled: !row.url,
    },
  ];

  const chips: { key: string; label: string }[] = [];
  for (const [key, facet] of facetDefs) {
    const value = params.get(key);
    if (!value) continue;
    if (facet.kind === "number") {
      const { min, max } = rangeFromParam(value);
      const unit = facet.unit === "pcs" ? "" : ` ${facet.unit}`;
      const text = min && max ? `${min}–${max}${unit}` : min ? `≥ ${min}${unit}` : `≤ ${max}${unit}`;
      chips.push({ key, label: `${facet.label} ${text}` });
    } else {
      const labels = valuesFromParam(value).map((item) => formatSpec(facet, item));
      chips.push({ key, label: `${facet.label}: ${labels.join(" or ")}` });
    }
  }

  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const count = data?.count ?? 0;
  const categoryInfo = data?.categories.find((entry) => entry.slug === shownCategory);
  const listCount = list.data?.lines ?? 0;
  const groupedCategories = new Set(taxonomy.data?.filter((entry) => entry.includes.length).map((entry) => entry.slug));
  const totalSupplies =
    data?.categories.reduce((sum, entry) => sum + (groupedCategories.has(entry.slug) ? 0 : entry.count), 0) ?? 0;
  const supplierFilter = data?.supplier_filter ?? [];
  const filtersActive = chips.length > 0 || Boolean(params.get("q")) || supplierFilter.length > 0;
  const asked = params.get("asked");
  const unread = params.get("unread");
  const reading = Boolean(params.get("text")) && !search.isError;
  const hasFilterChips = Boolean(categoryInfo) || chips.length > 0 || supplierFilter.length > 0;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Supplies"
        description="Vials, bottles, sprayers, pipettes, blotters, glassware and perfumer’s alcohol from every store we crawl, searchable by spec."
        meta={data && taxonomy.data ? <Badge>{pluralize(totalSupplies, "supply", "supplies")}</Badge> : null}
        actions={
          <ButtonLink to="/supplies/list" icon="cart">
            {listCount ? `Shopping list (${formatInteger(listCount)})` : "Shopping list"}
          </ButtonLink>
        }
      />

      <form className={styles.ask} onSubmit={ask} role="search">
        <TextField
          label="Describe what you need"
          type="search"
          placeholder="glass vials 5–15 ml with PTFE liners · clear perfume bottles 30–100 ml · FEA 15 pumps 70 µl"
          size="lg"
          prefix={<Icon name="search" />}
          value={draft}
          onValueChange={setDraft}
          className={styles.askField}
        />
        <Button type="submit" variant="primary" pending={reading} pendingLabel="Reading…">
          Search
        </Button>
      </form>

      {hasFilterChips || asked || unread ? (
        <div className={styles.chips}>
          {asked ? <span className={styles.asked}>“{asked}” reads as</span> : null}
          {hasFilterChips ? (
            <ul className={styles.chipList} aria-label="Filters in use">
              {categoryInfo ? (
                <li>
                  <Badge tone="accent">{categoryInfo.label}</Badge>
                </li>
              ) : null}
              {chips.map((chip) => (
                <li key={chip.key} className={styles.chip}>
                  {chip.label}
                  <IconButton
                    icon="x"
                    size="sm"
                    variant="ghost"
                    label={`Remove ${chip.label}`}
                    onClick={() => setParam(chip.key, null)}
                  />
                </li>
              ))}
              {supplierFilter.map((supplier) => (
                <li key={supplier.slug} className={styles.chip}>
                  Supplier: {supplier.name}
                  <IconButton
                    icon="x"
                    size="sm"
                    variant="ghost"
                    label={`Remove supplier ${supplier.name}`}
                    onClick={() => toggleSupplier(supplier.slug)}
                  />
                </li>
              ))}
            </ul>
          ) : null}
          {unread ? (
            <span className={styles.unread}>
              Not read: “{unread}”
              <Button variant="ghost" size="sm" onClick={() => searchTitles(unread)}>
                Search titles for “{unread}”
              </Button>
            </span>
          ) : null}
          <Button variant="ghost" size="sm" onClick={clearAll}>
            Clear all
          </Button>
        </div>
      ) : null}

      <nav className={styles.categories} aria-label="Supply categories">
        <button
          type="button"
          className={styles.category}
          aria-pressed={!shownCategory}
          onClick={() => chooseCategory("")}
        >
          All
        </button>
        {(data?.categories ?? []).map((entry) => (
          <button
            key={entry.slug}
            type="button"
            className={styles.category}
            aria-pressed={shownCategory === entry.slug}
            disabled={entry.count === 0 && shownCategory !== entry.slug}
            title={entry.description}
            onClick={() => chooseCategory(entry.slug)}
          >
            {entry.label}
            <span className={styles.categoryCount}>{formatInteger(entry.count)}</span>
          </button>
        ))}
      </nav>

      <div className={styles.layout}>
        <aside className={styles.filters} aria-label="Filter by spec">
          {data?.facets.length ? (
            <FacetPanel
              facets={data.facets}
              params={params}
              onFilterChange={(key, value) => setParam(key, value)}
              onUnknownChange={(key, include) =>
                update((next) => {
                  const keys = new Set(valuesFromParam(next.get("unknown")));
                  if (include) keys.add(key);
                  else keys.delete(key);
                  if (keys.size) next.set("unknown", [...keys].join(","));
                  else next.delete("unknown");
                })
              }
            />
          ) : (
            <p className={styles.filterHint}>
              Pick a category to filter by its specs: capacity, neck finish, liner, length…
            </p>
          )}
        </aside>

        <section className={styles.results} aria-label="Supplies found">
          <Toolbar label="Result options">
            <TextField
              label="Words in the name or SKU"
              hideLabel
              type="search"
              size="sm"
              placeholder="Words in the name or SKU…"
              value={params.get("q") ?? ""}
              onValueChange={(value) => setParam("q", value || null)}
              className={styles.words}
            />
            <Checkbox
              label="In stock"
              checked={params.get("in_stock") === "1"}
              onCheckedChange={(checked) => setParam("in_stock", checked ? "1" : null)}
            />
            <Checkbox
              label={`Ships to ${countryName(country)}`}
              checked={params.get("ships") === "1"}
              onCheckedChange={(checked) => setParam("ships", checked ? "1" : null)}
            />
            {data?.price_check_count || params.get("check") ? (
              <Checkbox
                label={`Prices to check (${formatInteger(data?.price_check_count ?? 0)})`}
                checked={params.get("check") === "1"}
                onCheckedChange={(checked) => setParam("check", checked ? "1" : null)}
              />
            ) : null}
            <Select
              label="Sort by"
              hideLabel
              size="sm"
              options={sortOptions(basis)}
              value={params.get("sort") ?? "unit_price"}
              onValueChange={(value) => setParam("sort", value && value !== "unit_price" ? value : null)}
            />
          </Toolbar>
          {data?.notes.map((note) => (
            <p key={note} className={styles.note}>
              {note}
            </p>
          ))}
          {data?.overview && shownCategory ? (
            <MarketOverview
              overview={data.overview}
              currency={data.currency}
              country={country}
              onToggleSupplier={toggleSupplier}
            />
          ) : null}
          <DataTable
            caption="Supplies found"
            columns={columns}
            rows={data?.items}
            getRowId={(row) => row.id}
            loading={search.isPending}
            error={search.error}
            onRetry={() => void search.refetch()}
            rowActions={rowActions}
            rowActionsLabel={(row) => `Actions for ${row.title}`}
            selectedRowId={params.get("item")}
            rowClassName={(row) => (row.offer.in_stock === false ? styles.outOfStock : undefined)}
            stickyHeader
            empty={
              <EmptyState
                variant="inline"
                headingLevel={3}
                icon="vial"
                title={filtersActive ? "No supplies match these filters" : "No supplies yet"}
                description={
                  filtersActive
                    ? "Remove a filter, or include the listings that don’t state a spec."
                    : "Supplies appear as the supply stores are crawled. Turn on their sync on the Suppliers page."
                }
              />
            }
          />
          {count > PAGE_SIZE ? (
            <div className={styles.pager}>
              <span className={styles.pageInfo}>
                {formatInteger((page - 1) * PAGE_SIZE + 1)}–{formatInteger(Math.min(page * PAGE_SIZE, count))} of{" "}
                {formatInteger(count)}
              </span>
              <Button
                size="sm"
                disabled={page <= 1}
                onClick={() => setParam("page", String(page - 1), { keepPage: true })}
              >
                Previous
              </Button>
              <Button
                size="sm"
                disabled={page * PAGE_SIZE >= count}
                onClick={() => setParam("page", String(page + 1), { keepPage: true })}
              >
                Next
              </Button>
            </div>
          ) : null}
        </section>
      </div>

      <SupplyDrawer itemId={params.get("item")} onClose={() => setParam("item", null, { keepPage: true })} />
    </div>
  );
}

const ADD_ON_VALUES_SHOWN = 2;

function SpecCell({ row, facet }: { row: SupplyRow; facet: FacetDef }) {
  const value = row.specs[facet.key];
  if (value === undefined || value === null) {
    const offered = (row.add_on_values[facet.key] ?? []).map((item) => formatSpec(facet, item, { withUnit: false }));
    if (offered.length) {
      const more = offered.length - ADD_ON_VALUES_SHOWN;
      return (
        <Tooltip content={`Not included; the store offers it as an add-on: ${offered.join(", ")}`}>
          <button type="button" className={clsx(styles.hint, styles.unknown)}>
            add-on: {offered.slice(0, ADD_ON_VALUES_SHOWN).join(", ")}
            {more > 0 ? ` +${more}` : ""}
          </button>
        </Tooltip>
      );
    }
    if (row.not_applicable.includes(facet.key)) {
      return (
        <Tooltip content="Doesn’t apply to this item">
          <button type="button" className={clsx(styles.hint, styles.unknown)}>
            n/a
          </button>
        </Tooltip>
      );
    }
    return row.unresolved.includes(facet.key) ? (
      <Tooltip content="The listing names more than one value">
        <button type="button" className={clsx(styles.hint, styles.unknown)}>
          Several
        </button>
      </Tooltip>
    ) : (
      <span className={styles.unknown}>{MISSING_VALUE}</span>
    );
  }
  return <>{formatSpec(facet, value, { withUnit: false })}</>;
}

/** The unit price, with its unit when the column's header doesn't give it (no category, or a solvent
 * sold by mass in a column per litre). */
function UnitPriceCell({ row, columnBasis }: { row: SupplyRow; columnBasis: UnitBasis | null }) {
  if (row.unit_price_minor === null) {
    if (row.offer.price_minor === null) return <span className={styles.unknown}>{MISSING_VALUE}</span>;
    return (
      <Tooltip content={missingUnitPriceReason(row.unit_basis)}>
        <button type="button" className={clsx(styles.hint, styles.unknown)}>
          {MISSING_VALUE}
        </button>
      </Tooltip>
    );
  }
  return (
    <Money
      minor={row.unit_price_minor}
      currency={row.offer.currency}
      extraDecimals={unitPriceDecimals(row.unit_basis)}
      per={row.unit_basis === columnBasis ? undefined : unitSymbol(row.unit_basis)}
    />
  );
}
